import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Rebid() {
  const [vendors, setVendors] = useState([]);
  const [picked, setPicked] = useState([]);
  const [filter, setFilter] = useState("");
  const [comparison, setComparison] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState({ title: "", scope: "", incumbent: "", owner: "" });
  const [decision, setDecision] = useState({ vendor: "", justification: "" });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadEvents = useCallback(() => {
    apiFetch("/sourcing/events").then((r) => (r.ok ? r.json() : [])).then(setEvents);
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/vendors").then((r) => (r.ok ? r.json() : [])).then((list) =>
      setVendors(Array.isArray(list) ? list : list.vendors || []));
    loadEvents();
  }, [navigate, loadEvents]);

  const toggle = (key) => {
    setPicked((p) => p.includes(key) ? p.filter((k) => k !== key)
                                     : p.length >= 4 ? p : [...p, key]);
  };

  const compare = async () => {
    setError(null);
    const res = await apiFetch(`/sourcing/compare?vendors=${picked.join(",")}`);
    const data = await res.json();
    if (!res.ok) { setError(typeof data.detail === "string" ? data.detail
                                                            : JSON.stringify(data.detail));
                   setComparison(null); return; }
    setComparison(data);
    setForm((f) => ({ ...f, incumbent: f.incumbent || picked[0] }));
  };

  const post = (path, body, label, after) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) {
        setStatus(`${label} failed — ${typeof data.detail === "string" ? data.detail
                                                                      : JSON.stringify(data.detail)}`);
        return;
      }
      setStatus(`${label} recorded`);
      loadEvents();
      if (after) after(data);
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="rebid-title">Rebid Workspace</h1>
          <p className="text-sm text-muted-foreground">
            Compare up to four vendors, then carry the evidence into a sourcing event.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="rebid-contracts-link">
            <Link to="/contracts">Contracts</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="rebid-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="rebid-status">{status}</p>}

        <section className="space-y-3" data-testid="rebid-vendor-picker">
          <h2 className="text-base md:text-lg font-medium">Pick two to four vendors</h2>
          <div className="space-y-1">
            <label htmlFor="rebid-filter" className="text-sm">Find a vendor</label>
            <Input id="rebid-filter" data-testid="rebid-filter" value={filter}
                   placeholder="Type part of a vendor name"
                   onChange={(e) => setFilter(e.target.value)} />
          </div>
          {picked.length > 0 && (
            <p className="text-sm" data-testid="rebid-picked">
              Selected: {picked.join(", ")}
            </p>
          )}
          <div className="grid gap-2 md:grid-cols-2">
            {vendors
              .filter((v) => picked.includes(v.vendor_key) ||
                             (v.name || "").toLowerCase().includes(filter.toLowerCase()) ||
                             v.vendor_key.includes(filter.toLowerCase()))
              .slice(0, 20)
              .map((v) => (
              <div key={v.vendor_key} className="flex items-center gap-2 border rounded-md px-3 py-2">
                <input id={`vendor-${v.vendor_key}`} type="checkbox"
                       data-testid={`rebid-vendor-${v.vendor_key}`}
                       checked={picked.includes(v.vendor_key)}
                       onChange={() => toggle(v.vendor_key)} />
                <label htmlFor={`vendor-${v.vendor_key}`} className="text-sm">{v.name}</label>
              </div>
            ))}
            {vendors.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="rebid-vendors-empty">
                No vendors recorded yet.
              </p>
            )}
          </div>
          <Button size="sm" data-testid="rebid-compare" disabled={picked.length < 2}
                  onClick={compare}>
            Compare {picked.length || 0} vendors
          </Button>
        </section>

        {comparison && (
          <section className="space-y-3" data-testid="rebid-comparison">
            <h2 className="text-base md:text-lg font-medium" data-testid="rebid-comparison-title">
              {comparison.name}
            </h2>
            <p className="text-sm text-muted-foreground" data-testid="rebid-comparison-note">
              {comparison.note}
            </p>
            {comparison.financials_withheld && (
              <p className="text-sm text-amber-800" data-testid="rebid-financials-withheld">
                {comparison.financials_withheld}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">Vendor comparison by measure</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Measure</th>
                    {comparison.vendors.map((v) => (
                      <th key={v.vendor_key} scope="col" className="px-3 py-2">{v.name}</th>
                    ))}
                    <th scope="col" className="px-3 py-2">How it is measured</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row) => (
                    <tr key={row.metric} className="border-b last:border-0"
                        data-testid={`rebid-row-${row.metric.replace(/\W+/g, "-").toLowerCase()}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium">{row.metric}</th>
                      {row.values.map((value, i) => (
                        <td key={i} className="px-3 py-2">
                          {value === null ? "not measured" : value}
                          {row.leads === i && (
                            <span className="ml-2 text-xs text-emerald-800 font-medium"
                                  data-testid={`rebid-leader-${i}`}>leads</span>
                          )}
                          {row.sample && row.sample[i] !== null && row.sample[i] !== undefined && (
                            <span className="block text-xs text-muted-foreground">
                              n={row.sample[i]}
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-xs text-muted-foreground">{row.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm" data-testid="rebid-leads-summary">
              Lines led: {Object.entries(comparison.leads)
                .map(([k, n]) => `${k}: ${n}`).join(" · ")} · tied {comparison.tied}
            </p>

            <div className="border rounded-md p-4 space-y-2" data-testid="rebid-event-form">
              <h3 className="text-sm font-semibold">Start a sourcing event from this comparison</h3>
              <div className="grid gap-2 md:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="event-title" className="text-sm">Title</label>
                  <Input id="event-title" data-testid="rebid-event-title" value={form.title}
                         onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="event-owner" className="text-sm">Accountable owner email</label>
                  <Input id="event-owner" data-testid="rebid-event-owner" value={form.owner}
                         onChange={(e) => setForm({ ...form, owner: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="event-scope" className="text-sm">Scope</label>
                  <Input id="event-scope" data-testid="rebid-event-scope" value={form.scope}
                         onChange={(e) => setForm({ ...form, scope: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="event-incumbent" className="text-sm">Incumbent</label>
                  <select id="event-incumbent" data-testid="rebid-event-incumbent"
                          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                          value={form.incumbent}
                          onChange={(e) => setForm({ ...form, incumbent: e.target.value })}>
                    <option value="">No incumbent</option>
                    {comparison.vendors.map((v) => (
                      <option key={v.vendor_key} value={v.vendor_key}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Button size="sm" variant="outline" data-testid="rebid-event-create"
                      onClick={() => post("/sourcing/events",
                                           { title: form.title, scope: form.scope,
                                             incumbent_vendor_key: form.incumbent,
                                             vendor_keys: comparison.vendors.map((v) => v.vendor_key),
                                             owner_email: form.owner },
                                           "Sourcing event", setSelectedEvent)}>
                Create sourcing event
              </Button>
            </div>
          </section>
        )}

        <section className="space-y-2" data-testid="rebid-events">
          <h2 className="text-base md:text-lg font-medium">Sourcing events</h2>
          <ul className="space-y-1">
            {events.map((e) => (
              <li key={e.id}>
                <button className="w-full text-left text-sm border rounded px-3 py-2 hover:border-foreground/40"
                        data-testid={`rebid-event-row-${e.number}`}
                        onClick={() => apiFetch(`/sourcing/events/${e.id}`)
                          .then((r) => (r.ok ? r.json() : null)).then(setSelectedEvent)}>
                  <span className="font-mono">{e.number}</span> · {e.title} ·{" "}
                  <span className="text-muted-foreground">{e.state}</span> ·{" "}
                  {e.vendor_keys.join(", ")}
                </button>
              </li>
            ))}
            {events.length === 0 && (
              <li className="text-sm text-muted-foreground" data-testid="rebid-events-empty">
                No sourcing events yet.
              </li>
            )}
          </ul>
        </section>

        {selectedEvent && (
          <section className="space-y-3 border-t pt-6" data-testid="rebid-event-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="rebid-event-detail-title">
                {selectedEvent.number} — {selectedEvent.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="rebid-event-detail-close"
                      onClick={() => setSelectedEvent(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="rebid-event-detail-meta">
              {selectedEvent.state} · scope: {selectedEvent.scope} · owner{" "}
              {selectedEvent.owner_email} · incumbent{" "}
              {selectedEvent.incumbent_vendor_key || "none"} · invited{" "}
              {selectedEvent.vendor_keys.join(", ")}
            </p>

            {selectedEvent.decision ? (
              <div className="text-sm space-y-1" data-testid="rebid-event-decision">
                <p>
                  Selected: <strong>{selectedEvent.decision.selected_vendor_key}</strong>{" "}
                  ({selectedEvent.decision.incumbent_retained ? "incumbent retained"
                                                              : "work moves"})
                </p>
                <p className="text-muted-foreground">{selectedEvent.decision.justification}</p>
                <p>
                  Applied to the incumbent's contracts as{" "}
                  {selectedEvent.decision.renewal_decision_applied || "no contract change"}:{" "}
                  {(selectedEvent.decision.contracts_decided || [])
                    .map((c) => c.number).join(", ") || "none"}
                </p>
                {(selectedEvent.decision.contracts_skipped || []).length > 0 && (
                  <p className="text-amber-800" data-testid="rebid-event-skipped">
                    Left alone: {selectedEvent.decision.contracts_skipped
                      .map((c) => `${c.number} (${c.reason})`).join("; ")}
                  </p>
                )}
              </div>
            ) : (
              <div className="border rounded-md p-4 space-y-2" data-testid="rebid-decision-form">
                <h3 className="text-sm font-semibold">Record the owner's selection</h3>
                <label htmlFor="decision-vendor" className="text-sm">Selected vendor</label>
                <select id="decision-vendor" data-testid="rebid-decision-vendor"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={decision.vendor}
                        onChange={(e) => setDecision({ ...decision, vendor: e.target.value })}>
                  <option value="">Choose…</option>
                  {selectedEvent.vendor_keys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <label htmlFor="decision-justification" className="text-sm">Justification</label>
                <Input id="decision-justification" data-testid="rebid-decision-justification"
                       value={decision.justification}
                       onChange={(e) => setDecision({ ...decision,
                                                      justification: e.target.value })} />
                <p className="text-xs text-muted-foreground">
                  Selecting the incumbent records a renewal on their active contracts; selecting
                  anyone else records a termination. HCD Legal remains authoritative.
                </p>
                <Button size="sm" variant="outline" data-testid="rebid-decision-save"
                        onClick={() => post(`/sourcing/events/${selectedEvent.id}/decision`,
                                             { selected_vendor_key: decision.vendor,
                                               justification: decision.justification },
                                             "Decision", setSelectedEvent)}>
                  Record decision
                </Button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
